/**
 * Block 13 PR-G Phase 6.5G A8 (2026-05-25) — Section «Спецификация»
 * (Приложение №1).
 *
 * Orchestrator над `<SpecBlockEditor/>` per блок мебели. State:
 *   - `state.spec[]` — array of `ContractSpecBlock`. Каждый блок имеет
 *     fixed row `block_cost` + custom rows (key='custom-<uuid>').
 *
 * **Phase 6.5G A8 (consolidation):** ОДНА dashed button «+ Добавить мебель»
 * (вместо 2 равноправных в 6.5F.2). Открывает `<ChoiceSheetCreateMode/>` →
 * пользователь выбирает:
 *   1. **«Взять данные из КП»** → chain в `<ChoiceSheetForQuotes/>` (existing
 *      flow) → on select → dispatch `ADD_SPEC_BLOCK { seedValues, customRows }`
 *      (atomic 1:1 КП copy через `buildSpecBlockSeedFromQuote`).
 *   2. **«Заполнить вручную»** → dispatch
 *      `ADD_SPEC_BLOCK { manualSeed: true, title: '' }` — reducer вызывает
 *      `buildManualSpecRows()` (17 default позиций из Block 12 + block_cost).
 *      User дописывает или удаляет лишние через SpecRowEditor.
 *
 * Удалена 6.5F.2 отдельная «Добавить из КП» dashed button — её функционал
 * теперь sub-choice в ChoiceSheetCreateMode.
 *
 * **ИТОГО card:** sum block.rows[].cost ?? 0 across all blocks via
 * `selectTotalSpec`. UI nominative form currency word per
 * `getCurrencyWord(state.currencyCode)`.
 *
 * Reference:
 *   - ADR-0025 Amendment A8.5 (ChoiceSheetCreateMode reuse pattern)
 *   - ADR-0025 Amendment A8.3 (ADD_SPEC_BLOCK manualSeed flag)
 */

import { type Dispatch, useState } from 'react';
import { Plus } from 'lucide-react';
import { getCurrencyShort, getCurrencyWord } from '@pomoshnik/shared';

import type { SavedQuote } from '@/features/calculator/quote/state/savedQuotesIdb';
import { genUuid } from '@/lib/uuid';
import { SectionCard } from '@/features/calculator/anketa/components/SectionCard';
import { SectionHeader } from '@/features/calculator/anketa/components/SectionHeader';

import { ChoiceSheetCreateMode } from '../components/ChoiceSheetCreateMode';
import { ChoiceSheetForQuotes } from '../components/ChoiceSheetForQuotes';
import { SpecBlockEditor } from '../components/SpecBlockEditor';
import { buildSpecBlockSeedFromQuote } from '../services/quoteToContractMapping';
import { selectTotalSpec } from '../state/contractSelectors';
import type { ContractAction, ContractFormState } from '../state/contractReducer';

// Phase 7 hotfix round 4 (R4-#1, 2026-05-26) — `touch-manipulation` aligned с
// SpecBlockEditor dashed button: iOS ghost-click prevention + 300ms tap delay
// removal. См. SpecBlockEditor.tsx для root cause analysis.
const DASHED_BUTTON_CLASS =
  'w-full min-h-tap-chip rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold ' +
  'flex items-center justify-center gap-2 touch-manipulation focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80';

export interface SectionSpecProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
  /** PR-C5 (ADR-0039) — родной КП новее последнего сева → ссылка «↻» в родном блоке. */
  reseedAvailable?: boolean;
  /** PR-C5 — клик «↻ Обновить из КП» (тот же путь, что «Да» в модалке). */
  onReseedFromQuote?: () => void;
}

export function SectionSpec({
  state,
  dispatch,
  reseedAvailable = false,
  onReseedFromQuote,
}: SectionSpecProps) {
  // Phase 6.5G A8 — chained sheet state (Create choice → optional Quotes
  // choice → dispatch).
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isQuotesSheetOpen, setIsQuotesSheetOpen] = useState(false);

  // Phase 6.5B Amendment A1 — currency short/word from draft.currencyCode.
  const currencyShort = getCurrencyShort(state.currencyCode);
  const currencyWord = getCurrencyWord(state.currencyCode);
  const total = selectTotalSpec(state);

  function makeBlockHandlers(blockId: string) {
    return {
      onUpdateTitle: (value: string) =>
        dispatch({ type: 'UPDATE_SPEC_BLOCK_TITLE', blockId, title: value }),
      onRemoveBlock: () => dispatch({ type: 'REMOVE_SPEC_BLOCK', blockId }),
      onAddCustomRow: (rowId: string) => dispatch({ type: 'ADD_CUSTOM_SPEC_ROW', blockId, rowId }),
      onUpdateRowLabel: (rowId: string, value: string) =>
        dispatch({
          type: 'UPDATE_SPEC_ROW',
          blockId,
          rowId,
          update: { field: 'label', value },
        }),
      onUpdateRowType: (rowId: string, value: string | undefined) =>
        dispatch({
          type: 'UPDATE_SPEC_ROW',
          blockId,
          rowId,
          update: { field: 'type', value },
        }),
      onUpdateRowBrand: (rowId: string, value: string | undefined) =>
        dispatch({
          type: 'UPDATE_SPEC_ROW',
          blockId,
          rowId,
          update: { field: 'brand', value },
        }),
      onUpdateRowCost: (rowId: string, value: number | undefined) =>
        dispatch({
          type: 'UPDATE_SPEC_ROW',
          blockId,
          rowId,
          update: { field: 'cost', value },
        }),
      onRemoveRow: (rowId: string) => dispatch({ type: 'REMOVE_SPEC_ROW', blockId, rowId }),
    };
  }

  function handleSelectFromQuote() {
    // Chained — Create choice closes (через onClose), затем opens Quotes.
    setIsQuotesSheetOpen(true);
  }

  function handleSelectManual() {
    // Phase 6.5G A8 — manual seed → 17 default позиций + block_cost.
    dispatch({ type: 'ADD_SPEC_BLOCK', id: genUuid(), title: '', manualSeed: true });
  }

  function handleQuoteSelected(quote: SavedQuote) {
    const seed = buildSpecBlockSeedFromQuote(quote);
    dispatch({
      type: 'ADD_SPEC_BLOCK',
      id: genUuid(),
      title: seed.title,
      seedValues: seed.seedValues,
      customRows: seed.customRows,
    });
  }

  return (
    <SectionCard testId="contract-section-spec">
      <SectionHeader>Приложение №1 — Спецификация</SectionHeader>

      {state.spec.length === 0 ? (
        <p
          className="text-sm text-ink/60 text-center py-2 mb-3"
          data-testid="contract-section-spec-empty"
        >
          Список пуст. Нажмите «+ Добавить мебель» чтобы добавить блок.
        </p>
      ) : (
        <div data-testid="contract-section-spec-blocks">
          {state.spec.map((block, idx) => {
            // PR-C5 (ADR-0039) — ссылка «↻ Обновить из КП» только в РОДНОМ блоке
            // (sourceQuoteId === fromQuoteId) и только когда КП новее сева.
            const isNative =
              state.fromQuoteId !== null && block.sourceQuoteId === state.fromQuoteId;
            return (
              <SpecBlockEditor
                key={block.id}
                block={block}
                index={idx + 1}
                currencyShort={currencyShort}
                testIdPrefix={`spec-block-${block.id}`}
                showReseedLink={isNative && reseedAvailable}
                onReseedFromQuote={onReseedFromQuote}
                {...makeBlockHandlers(block.id)}
              />
            );
          })}
        </div>
      )}

      {/* Phase 6.5G A8 — ОДНА dashed button «+ Добавить мебель» (consolidated).
          Открывает ChoiceSheetCreateMode → user выбирает «Взять данные из КП»
          либо «Заполнить вручную». */}
      <button
        type="button"
        onClick={() => setIsCreateSheetOpen(true)}
        aria-label="Добавить блок мебели"
        data-testid="contract-section-spec-add-furniture"
        className={DASHED_BUTTON_CLASS}
      >
        <Plus size={18} aria-hidden="true" />+ Добавить мебель
      </button>

      <ChoiceSheetCreateMode
        isOpen={isCreateSheetOpen}
        onClose={() => setIsCreateSheetOpen(false)}
        onSelectFromQuote={handleSelectFromQuote}
        onSelectManual={handleSelectManual}
        title="Добавить мебель"
        subtitle="Выберите источник позиций"
      />

      <ChoiceSheetForQuotes
        isOpen={isQuotesSheetOpen}
        onClose={() => setIsQuotesSheetOpen(false)}
        onSelect={handleQuoteSelected}
        title="Выберите КП"
        subtitle="Заполненные позиции из КП попадут в новый блок спецификации"
      />

      <div
        data-testid="contract-section-spec-total"
        className="mt-4 rounded-xl border-[1.5px] border-accent bg-card px-4 py-3 flex items-center justify-between"
      >
        <span className="text-sm font-bold text-ink uppercase tracking-wide">ИТОГО</span>
        <span className="text-base font-bold text-ink">
          {total.toLocaleString('ru-RU')} {currencyWord}
        </span>
      </div>
    </SectionCard>
  );
}
